import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useStartSession } from "@trainwell/features/analytics";
import {
  useClient,
  useUpdateSurveyData,
  type UpdateSurveyDataBody,
} from "@trainwell/features/clients";
import {
  useEnrollFreemium,
  useJoinByCode,
} from "@trainwell/features/freemium-plans";
import { useEffect, useState } from "react";
import BasicLayout from "src/components/BasicLayout";
import { useErrorState } from "src/components/ErrorState";
import { useJoinState } from "src/components/join/JoinState";
import { useUserState } from "src/components/UserState";
import type { SelectionButtonProps } from "src/features/SelectionButtonProps";
import { useCurrentUserId } from "src/features/useCurrentUserId";
import { useButtonStore } from "../components/ButtonState";

export const Route = createFileRoute("/_layout")({
  component: LayoutComponent,
});

function LayoutComponent() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const urmSource = searchParams.get("utm_source");
  const urmMedium = searchParams.get("utm_medium");
  const urmCampaign = searchParams.get("utm_campaign");
  const urmContent = searchParams.get("utm_content");
  const referrerUserId = searchParams.get("referring_user_id");

  const { data, error, isPending, refetch } = useStartSession({
    startSessionData: {
      referrerUserId: referrerUserId ?? undefined,
      platform: "web_freemium",
      utmSource: urmSource ?? undefined,
      utmMedium: urmMedium ?? undefined,
      utmCampaign: urmCampaign ?? undefined,
      utmContent: urmContent ?? undefined,
    },
    queryConfig: {
      // Only start a new session if there is no user ID in session storage.
      enabled:
        !sessionStorage.getItem("tempUserId") &&
        !sessionStorage.getItem("userId"),
    },
  });

  const { mutate: updateSurveyData } = useUpdateSurveyData({
    mutationConfig: {
      onSuccess: (data) => {
        console.log("Survey data updated successfully:", data);
      },
      onError: (error) => {
        console.error("Error updating survey data:", error);
      },
    },
  });

  const theme = useTheme();

  const navigate = useNavigate();

  const [buttonText, setButtonText] = useState("Continue");
  const isButtonEnabled = useButtonStore((state) => state.isButtonEnabled);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const userId = useCurrentUserId();
  const setTempUserId = useUserState((state) => state.setTempUserId);
  const setUserId = useUserState((state) => state.setUserId);
  const selections = useUserState((state) => state.selections);
  const firstName = useUserState((state) => state.firstName);
  const lastName = useUserState((state) => state.lastName);
  const email = useUserState((state) => state.email);
  const phoneNumber = useUserState((state) => state.phoneNumber);
  const profile = useUserState((state) => state.profile);
  const [currentStep, setCurrentStep] = useState(1);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const setErrorMessage = useErrorState((state) => state.setErrorMessage);
  const setButtonVisible = useButtonStore((state) => state.setVisible);
  const isButtonVisible = useButtonStore((state) => state.isVisible);
  const teamCode = useJoinState((state) => state.code);

  const { data: client } = useClient({
    userId: userId,
    queryConfig: { enabled: !!userId },
  });

  // Save user ID that was returned when analytics session was started.
  useEffect(() => {
    if (data) {
      // Start a new session, navigating back to the first page.
      console.log("Session started:", data);
      setTempUserId(data.userId);
      sessionStorage.setItem("tempUserId", data.userId);
      // Update Sentry.
      Sentry.setUser({ id: data.userId });
      navigate({ to: "/" });
    }
    if (error) {
      console.error("Error starting session:", error);
    }
  }, [data, error, setTempUserId]);

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    const tempUserId = sessionStorage.getItem("tempUserId");
    if (tempUserId) {
      console.log("Restoring temp user ID:", tempUserId);
      setTempUserId(tempUserId);
    }
    if (userId) {
      console.log("Restoring actual user ID:", userId);
      setUserId(userId);
    }
  }, [setUserId, setTempUserId]);

  // Decide which text to show.
  useEffect(() => {
    const currentPath = window.location.pathname;

    // Decide which text to show on the button.
    if (currentPath === "/" || currentPath === "/intro") {
      setButtonText("Get started");
    } else if (currentPath === "/trainingPlanDetails") {
      setButtonText("Start this plan");
    } else if (currentPath === "/signUp") {
      setButtonText("Get my plan");
    } else {
      setButtonText("Continue");
    }

    //Hide the button on the training plans page.
    if (currentPath === "/trainingPlans" || currentPath === "signUp") {
      setButtonVisible(false);
    } else {
      console.log("Showing button");
      setButtonVisible(true);
    }

    // Decide which image to show on the left side of the screen.
    if (currentPath === "/" || currentPath === "/intro") {
      setImageUrl("/assets/onboarding-images/page-1.jpg");
      setShowProgressBar(false);
    } else if (currentPath === "/trainingStyle") {
      setImageUrl("/assets/onboarding-images/page-2.jpg");
      setShowProgressBar(true);
      setCurrentStep(1);
    } else if (currentPath === "/equipment") {
      setImageUrl("/assets/onboarding-images/page-3.jpg");
      setShowProgressBar(true);
      setCurrentStep(2);
    } else if (currentPath === "/experienceLevel") {
      setImageUrl("/assets/onboarding-images/page-4.jpg");
      setShowProgressBar(true);
      setCurrentStep(3);
    } else if (currentPath === "/profile") {
      setImageUrl("/assets/onboarding-images/page-4.jpg");
      setShowProgressBar(true);
      setCurrentStep(4);
    } else if (currentPath === "/trainerGender") {
      setImageUrl("/assets/onboarding-images/page-5.jpg");
      setShowProgressBar(true);
      setCurrentStep(5);
    } else if (currentPath === "/fitnessGoal") {
      setImageUrl("/assets/onboarding-images/page-6.jpg");
      setShowProgressBar(true);
      setCurrentStep(6);
    } else if (currentPath === "/daysOfWeek") {
      setImageUrl("/assets/onboarding-images/page-6.jpg");
      setShowProgressBar(false);
      setCurrentStep(6);
    } else {
      setShowProgressBar(false);
      setImageUrl(null);
    }
  }, [location.pathname, setButtonVisible]);

  // Function to map selections to the correct survey data structure.
  const mapSelectionsToSurveyData = (currentPath: string): any => {
    const surveyData: UpdateSurveyDataBody = {};

    if (currentPath === "/trainingStyle") {
      surveyData.freemiumTrainingStyle = mapTrainingStyle(
        selections["/trainingStyle"],
      );
    } else if (currentPath === "/equipment") {
      // Let mapEquipment handle the type conversion correctly
      surveyData.preCopilotEquipment = mapEquipment(selections["/equipment"]);
    } else if (currentPath === "/experienceLevel") {
      surveyData.freemiumExperienceLevel = mapExperienceLevel(
        selections["/experienceLevel"],
      );
    } else if (currentPath === "/profile") {
      if (profile) {
        surveyData.weightSystem = profile.weightSystem;
        surveyData.age = parseInt(profile.age);
        surveyData.height = parseInt(profile.heightInches);
        surveyData.weight = parseInt(profile.weight);
        surveyData.biologicalSex = profile.biologicalSex ?? "prefer_not_to_say";
      }
    } else if (currentPath === "/trainerGender") {
      surveyData.freemiumTrainerGender = mapTrainerGender(
        selections["/trainerGender"],
      );
    } else if (currentPath === "/fitnessGoal") {
      surveyData.freemiumPrimaryGoal = mapFitnessGoal(
        selections["/fitnessGoal"],
      );
    } else if (currentPath === "/daysOfWeek") {
      // These selections are not part of the SurveyData type yet
      // We'll just log them for now
      surveyData.freemiumDaysOfWeek = mapDaysOfWeek(selections["/daysOfWeek"]);
    }

    console.log("Mapped survey data:", surveyData);
    return surveyData;
  };

  // Update the survey data for the current path. Only relevant for some pages.
  const updateSurveyDataForPath = (currentPath: string) => {
    if (userId) {
      console.log(
        currentPath,
        ": ",
        userId,
        " Updating survey data with selections: ",
        mapSelectionsToSurveyData(currentPath),
      );
      updateSurveyData({
        userId: userId,
        data: mapSelectionsToSurveyData(currentPath),
      });
    }
  };

  const { mutate: joinTeam } = useJoinByCode({
    mutationConfig: {
      onSuccess: (data) => {
        console.log("Team joined successfully:", data);
      },
      onError: (error: any) => {
        console.error("Error joining team:", error);
      },
    },
  });

  const { mutate: createFreemium } = useEnrollFreemium({
    mutationConfig: {
      onSuccess: (data) => {
        console.log("Freemium account created successfully:", data);
        sessionStorage.setItem("userId", data.user_id);
        setUserId(data.user_id);
        setTempUserId(undefined);

        // If the user has joined the team via the invite link, then the isNew
        // field will be populated. When they make it to the end of this flow,
        // make sure to add them to the team.
        const isFirstTime = selections["/join/isNew"]?.[0]?.id === "first_time";
        console.log("Is first time:", isFirstTime);
        console.log("Team code:", teamCode);
        if (isFirstTime && teamCode) {
          joinTeam({
            code: teamCode,
            user_id: data.user_id,
          });
        }
        // Emit the request to create a team for new users for now.
        void navigate({ to: "/create/qrCode" });
      },
      onError: (error: any) => {
        console.error("Error creating freemium account:", error);
        // Only show specific message for 404 errors
        if (error.status_code === 400) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage("Something went wrong. Please try again.");
        }
      },
    },
  });

  // Decide which page is next.
  const nextPage = () => {
    const currentPath = window.location.pathname;
    console.log("All selections:", selections);
    setErrorMessage(undefined);

    if (currentPath === "/" || currentPath === "/intro") {
      void navigate({ to: "/trainingStyle" });
    } else if (currentPath === "/trainingStyle") {
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/equipment" });
    } else if (currentPath === "/equipment") {
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/experienceLevel" });
    } else if (currentPath === "/experienceLevel") {
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/profile" });
    } else if (currentPath === "/profile") {
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/trainerGender" });
    } else if (currentPath === "/trainerGender") {
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/fitnessGoal" });
    } else if (currentPath === "/fitnessGoal") {
      void queryClient.invalidateQueries({
        queryKey: ["freemium_plan_matches"],
      });
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/trainingPlans" });
    } else if (currentPath === "/trainingPlanDetails") {
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/daysOfWeek" });
    } else if (currentPath === "/daysOfWeek") {
      updateSurveyDataForPath(currentPath);
      void navigate({ to: "/signUp" });
    }
    if (currentPath === "/signUp") {
      try {
        // If the call is successful this navigates to creating teams.
        createFreemium({
          userId: userId || "",
          data: {
            first_name: firstName || "",
            last_name: lastName || "",
            email: email || "",
            phone_number: phoneNumber || "",
            plan_id: client?.freemium_plan_id || "",
          },
        });
      } catch (error) {
        console.error("Error creating freemium account:", error);
        setErrorMessage("Something went wrong. Please try again.");
      }
    }
  };

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  if (!isPending && !userId && error) {
    //Error state, we need to manually run start session
    sessionStorage.removeItem("userId");
    sessionStorage.removeItem("tempUserId");
    void refetch();
    return <Navigate to="/" />;
  }

  return (
    <BasicLayout
      imageUrl={imageUrl}
      isDesktop={isDesktop}
      enableButton={isButtonEnabled}
      isButtonVisible={isButtonVisible}
      buttonText={buttonText}
      showProgressBar={showProgressBar}
      currentStep={currentStep}
      totalSteps={6}
      onNextPage={nextPage}
      theme={theme}
    >
      <Outlet />
    </BasicLayout>
  );
}

function mapTrainingStyle(
  styles: SelectionButtonProps[] | undefined,
):
  | (
      | "traditional_strength"
      | "functional_strength"
      | "yoga"
      | "hiit"
      | "cardio"
      | "calisthenics"
    )[]
  | undefined {
  if (styles)
    return styles.map(
      (style) =>
        style.id as
          | "traditional_strength"
          | "functional_strength"
          | "yoga"
          | "hiit"
          | "cardio"
          | "calisthenics",
    );
  return undefined;
}

function mapExperienceLevel(
  experience: SelectionButtonProps[] | undefined,
): "beginner" | "novice" | "intermediate" | "advanced" | undefined {
  if (experience && experience.length > 0 && experience[0]?.id)
    return experience[0].id as
      | "beginner"
      | "novice"
      | "intermediate"
      | "advanced";
  return undefined;
}

function mapTrainerGender(
  genders: SelectionButtonProps[] | undefined,
): "male" | "female" | "no_preference" | undefined {
  console.log("Gender", genders);
  if (genders && genders.length > 0 && genders[0]?.id)
    return genders[0].id as "male" | "female" | "no_preference";
  else return "no_preference";
}

function mapFitnessGoal(
  goals: SelectionButtonProps[] | undefined,
):
  | "boost_cardio"
  | "build_strength"
  | "lose_fat"
  | "improve_health"
  | undefined {
  if (goals && goals.length > 0 && goals[0]?.id)
    return goals[0].id as
      | "boost_cardio"
      | "build_strength"
      | "lose_fat"
      | "improve_health";
  return undefined;
}

function mapEquipment(
  equipment: SelectionButtonProps[] | undefined,
): "none" | "few_at_home" | "apartment_gym" | "full_gym_access" | undefined {
  if (equipment && equipment.length > 0 && equipment[0]?.id)
    return equipment[0].id as
      | "none"
      | "few_at_home"
      | "apartment_gym"
      | "full_gym_access";
  return undefined;
}

function mapDaysOfWeek(
  daysOfWeek: SelectionButtonProps[] | undefined,
): number[] {
  if (daysOfWeek && daysOfWeek.length > 0) {
    // Map day names to their numeric values (Sunday = 0, Monday = 1, etc.)
    const dayIdToNumber: Record<string, number> = {
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6,
    };

    return daysOfWeek.map((day) => dayIdToNumber[day.id]);
  }
  return [];
}
